import { useState, useCallback } from 'react';
import {
  Box,
  Flex,
  Button,
  Icon,
  Input,
  useDisclosure,
  Tooltip,
} from '@chakra-ui/react';
import { Upload01 } from '@kula-ai/icons/dual-tone/Upload01';
import { PlusCircle } from '@kula-ai/icons/dual-tone/PlusCircle';
import { SearchMd } from '@kula-ai/icons/outline/SearchMd';
import { KText } from '@/ui-library/components/text';
import { KInput } from '@/ui-library/components/atoms/KInput';
import { KPopover } from '@/ui-library/components/organisms/KPopover/KPopover';
import { AttachmentList } from './AttachmentList';
import { Divider, getAttachmentKey } from './helpers';
import { useFileValidation } from '@/hooks/useFileValidation';
import { useShowToast } from '@/hooks/useShowToast';
import { X } from '@kula-ai/icons-core';

const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const SelectDocument = ({
  selectableAttachments,
  existingDocuments,
  onDocumentsAdded,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const resetAndClosePopover = useCallback(() => {
    setSelectedAttachments([]);
    setSearchQuery('');
    onClose();
  }, [onClose]);

  const handleToggleAttachment = useCallback((attachment) => {
    setSelectedAttachments((prev) => {
      const key = getAttachmentKey(attachment);
      const exists = prev.some((a) => getAttachmentKey(a) === key);
      return exists
        ? prev.filter((a) => getAttachmentKey(a) !== key)
        : [...prev, attachment];
    });
  }, []);

  const handleConfirmSelection = useCallback(() => {
    const newDocs = selectedAttachments.map((att) => ({
      ...att,
      name: att.filename,
    }));
    onDocumentsAdded(newDocs);
    resetAndClosePopover();
  }, [selectedAttachments, onDocumentsAdded, resetAndClosePopover]);

  const toast = useShowToast();
  const { handleAttachmentValidation } = useFileValidation();

  const handleFileUpload = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast({
          title: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit`,
          status: 'error',
        });
        event.target.value = '';
        return;
      }

      const isValid = await handleAttachmentValidation({ files: [file] });
      if (!isValid) {
        event.target.value = '';
        return;
      }

      onDocumentsAdded([
        {
          type: 'uploaded_file',
          filename: file.name,
          content_type: file.type,
          file,
          name: file.name,
        },
      ]);

      event.target.value = '';
    },
    [onDocumentsAdded, toast, handleAttachmentValidation]
  );

  const hasAttachments = selectableAttachments.length > 0;

  return (
    <Flex alignItems="center" my="16px" gap="16px">
      <Divider />
      <Flex gap="12px" justifyContent="center">
        <KPopover
          popoverActions={{
            isOpen,
            onOpen,
            onClose: resetAndClosePopover,
          }}
          popoverProps={{ placement: 'bottom-start' }}
          popoverContentProps={{
            width: '400px',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'border-color',
            _focus: { outline: 'none' },
          }}
          popoverBodyProps={{ p: 0 }}
          showArrow={false}
          showCloseButton={false}
          triggerElement={
            <Box display="inline-block">
              <Tooltip
                label="No documents available from application"
                isDisabled={hasAttachments}
                hasArrow
                placement="top"
              >
                <Button
                  variant="outline"
                  size="sm"
                  h="36px"
                  borderRadius="6px"
                  borderColor="border-color"
                  color="secondary-700"
                  onClick={hasAttachments ? onOpen : undefined}
                  isDisabled={!hasAttachments}
                  _hover={{ bg: 'secondary-25' }}
                >
                  <Icon
                    as={PlusCircle}
                    mr="6px"
                    boxSize="16px"
                    color="secondary-500"
                  />
                  Add from application
                </Button>
              </Tooltip>
            </Box>
          }
          footer={
            <Flex
              borderTop="1px solid"
              borderColor="border-color"
              p="16px 20px"
              justifyContent="flex-end"
              gap="12px"
              w="100%"
            >
              <Button
                variant="outline"
                size="sm"
                onClick={resetAndClosePopover}
                borderRadius="6px"
                color="primary-500"
                borderColor="primary-500"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                isDisabled={selectedAttachments.length === 0}
                onClick={handleConfirmSelection}
                borderRadius="6px"
                bg="primary-500"
                color="white"
                _hover={{ bg: 'primary-600' }}
              >
                Add selected
              </Button>
            </Flex>
          }
        >
          <Box>
            <Flex
              borderBottom="1px solid"
              borderColor="border-color"
              py="16px"
              px="20px"
              justifyContent="space-between"
              alignItems="center"
            >
              <KText size="md-semibold" fontWeight="550" color="secondary-900">
                Add documents from application
              </KText>
              <Icon
                as={X}
                cursor="pointer"
                onClick={resetAndClosePopover}
                color="secondary-500"
                w="20px"
                h="20px"
              />
            </Flex>

            <Box p="16px 0 0 0">
              <Box px="20px" mb="12px">
                <KInput
                  leftIcon={SearchMd}
                  placeholder="Search documents"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="sm"
                  bg="white"
                />
              </Box>

              <Box maxH="300px" overflowY="auto" px="20px" pb="16px">
                <AttachmentList
                  attachments={selectableAttachments}
                  searchQuery={searchQuery}
                  selectedAttachments={selectedAttachments}
                  existingDocuments={existingDocuments}
                  onToggle={handleToggleAttachment}
                />
              </Box>
            </Box>
          </Box>
        </KPopover>

        <Box position="relative" cursor="pointer">
          <Input
            type="file"
            onChange={handleFileUpload}
            position="absolute"
            top="0"
            left="0"
            width="100%"
            height="100%"
            opacity={0}
            cursor="pointer"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            zIndex={1}
            sx={{
              '&::-webkit-file-upload-button': { cursor: 'pointer' },
              '&::file-selector-button': { cursor: 'pointer' },
            }}
          />
          <Button
            variant="outline"
            size="sm"
            h="36px"
            borderRadius="6px"
            borderColor="border-color"
            color="secondary-700"
            _hover={{ bg: 'secondary-25' }}
          >
            <Icon as={Upload01} mr="6px" boxSize="16px" color="secondary-500" />
            Upload
          </Button>
        </Box>
      </Flex>
      <Divider />
    </Flex>
  );
};
